/**
 * project-white-label — Surface Layer (/01)
 *
 * Maps a project's free-form `white_label` record (from `GET /api/projects`)
 * into a validated `TenantThemeOverride` so the active project re-brands the
 * surface via `CarbonSurfaceThemeProvider.setTenantOverride`. Theme payloads
 * are untrusted input: only recognized keys are extracted and the provider's
 * `validateThemePayload` runs before any CSS custom property is injected.
 *
 * Recognized keys:
 *   baseTheme   — 'g100' | 'g90' | 'g10' | 'white'
 *   forced      — boolean (locks the user out of theme edits)
 *   tokens      — a map of cds-* token names to CSS strings
 *   brand_color — convenience -> cds-interactive + cds-button-primary
 *   font_family — convenience -> cds-font-family-sans
 */

import type { CarbonThemeName, ThemeTokenOverrides, TenantThemeOverride } from '@contracts/theme.contract';

const THEMES: readonly CarbonThemeName[] = ['g100', 'g90', 'g10', 'white'];

export function whiteLabelToTenantOverride(
  white: Record<string, unknown> | null,
  tenantId: string,
  nowIso: string,
): TenantThemeOverride | null {
  if (!white || typeof white !== 'object') return null;

  const tokenMap: Record<string, string> = {};
  if (white.tokens && typeof white.tokens === 'object') {
    for (const [key, value] of Object.entries(white.tokens as Record<string, unknown>)) {
      if (typeof value === 'string') tokenMap[key] = value;
    }
  }
  if (typeof white.brand_color === 'string') {
    tokenMap['cds-interactive'] = white.brand_color;
    tokenMap['cds-button-primary'] = white.brand_color;
  }
  if (typeof white.font_family === 'string') {
    tokenMap['cds-font-family-sans'] = white.font_family;
  }

  const baseTheme = THEMES.includes(white.baseTheme as CarbonThemeName)
    ? (white.baseTheme as CarbonThemeName)
    : undefined;
  const hasTokens = Object.keys(tokenMap).length > 0;
  if (!baseTheme && !hasTokens) return null;

  return {
    schemaVersion: 1,
    tenantId,
    baseTheme,
    forced: white.forced === true,
    tokens: hasTokens ? (tokenMap as ThemeTokenOverrides) : undefined,
    updatedAt: nowIso,
    updatedByIdentityId: 'white-label',
  };
}
